import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import ChatImage from './ChatImage';
import VoiceMessage from './VoiceMessage';
import { REACTION_EMOJIS } from '../chatConstants';
import { formatFileSize, formatTime, messagePreview } from '../chatUtils';

/**
 * Статус своего сообщения: часы — ещё не подтверждено сервером, одна галочка —
 * доставлено, двойная — собеседник прочитал. Статус считается от курсора чата,
 * а не хранится на сообщении: при сдвиге курсора галочки обновляются разом
 * у всех сообщений ниже нового значения.
 */
const MessageStatus = ({ message, own, peerReadCursor }) => {
  if (!own) return null;
  if (message.failed) return <Icon name="alert-triangle" size={12} />;
  if (message.pending || message.id == null) return <Icon name="clock" size={12} />;

  return (peerReadCursor ?? 0) >= message.id
    ? <Icon name="check-check" size={13} className="chat-check chat-check--read" />
    : <Icon name="check" size={12} className="chat-check" />;
};

/**
 * Пузырь сообщения: своё справа, чужое слева. Реакции — по наведению
 * (или долгому нажатию на тач-устройствах), ответ — из контекстного меню.
 */
const MessageBubble = ({
  message, own, showTail, currentUserId, peerReadCursor, onReply, onReact, onOpenImage,
  onJumpToReply, locale,
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);

  const startLongPress = () => {
    setLongPressTimer(setTimeout(() => setPickerOpen(true), 450));
  };
  const cancelLongPress = () => {
    if (longPressTimer) clearTimeout(longPressTimer);
    setLongPressTimer(null);
  };

  const handleReact = (emoji) => {
    setPickerOpen(false);
    onReact?.(message, emoji);
  };

  return (
    <div
      className={`chat-msg-row${own ? ' own' : ''}${showTail ? ' tail' : ''}`}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchMove={cancelLongPress}
      onContextMenu={(e) => {
        e.preventDefault();
        setPickerOpen((v) => !v);
      }}
    >
      <div className="chat-msg-body">
        <div className={`chat-bubble${own ? ' own' : ''}${message.failed ? ' failed' : ''}`}>
          {message.replyTo && (
            <button
              type="button"
              className="chat-reply-quote"
              onClick={() => onJumpToReply?.(message.replyTo.id)}
            >
              <span className="chat-reply-quote-bar" />
              <span className="chat-reply-quote-text">
                {message.replyTo.snippet || messagePreview(message.replyTo)}
              </span>
            </button>
          )}

          {message.messageType === 'IMAGE' && (
            <ChatImage message={message} onOpen={onOpenImage} />
          )}

          {message.messageType === 'VOICE' && <VoiceMessage message={message} />}

          {message.messageType === 'FILE' && message.attachment && (
            <a
              className="chat-file"
              href={message.attachment.url}
              target="_blank"
              rel="noreferrer"
              download={message.attachment.fileName}
            >
              <span className="chat-file-icon">
                <Icon name="file-text" size={18} />
              </span>
              <span className="chat-file-meta">
                <span className="chat-file-name">{message.attachment.fileName}</span>
                <span className="chat-file-size">{formatFileSize(message.attachment.fileSize)}</span>
              </span>
            </a>
          )}

          {message.content && <span className="chat-bubble-text">{message.content}</span>}

          <span className="chat-bubble-time">
            {formatTime(message.createdAt, locale)}
            <MessageStatus message={message} own={own} peerReadCursor={peerReadCursor} />
          </span>
        </div>

        {message.reactions?.length > 0 && (
          <div className={`chat-reactions${own ? ' own' : ''}`}>
            {message.reactions.map((reaction) => {
              const mine = (reaction.userIds || []).some(
                (id) => String(id) === String(currentUserId)
              );
              return (
                <button
                  key={reaction.emoji}
                  type="button"
                  className={`chat-reaction${mine ? ' mine' : ''}`}
                  onClick={() => handleReact(reaction.emoji)}
                >
                  <span>{reaction.emoji}</span>
                  <span className="chat-reaction-count">{reaction.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="chat-msg-tools">
        <button
          type="button"
          className="chat-tool-btn"
          title={t('chat.message.reply')}
          onClick={() => onReply?.(message)}
          disabled={message.id == null}
        >
          <Icon name="reply" size={14} />
        </button>
        <button
          type="button"
          className="chat-tool-btn"
          title={t('chat.message.react')}
          onClick={() => setPickerOpen((v) => !v)}
          disabled={message.id == null}
        >
          <Icon name="smile" size={14} />
        </button>

        {pickerOpen && (
          <div className="chat-reaction-picker">
            {REACTION_EMOJIS.map((emoji) => (
              <button key={emoji} type="button" onClick={() => handleReact(emoji)}>
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
